/* STEP 4 — TAT. Per-zone Min/Max editor (restored from master), seeded from the
   standard band. Edited cells turn amber; values slower than the standard range
   flag red but are allowed; min>max blocks. On Next the zones are serialized to a
   TAT CSV (zone,Min Tat days,Max Tat Days) and pushed through the real upload
   (type=TAT, applied immediately — no effective-from scheduling). */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'
import {useToast} from '../components'
import {TAT_ZONES, stdCell, buildSeededTat, tatBand} from './wizardUtils'
import DeltaPanel from './DeltaPanel'
import WizardFooter from './WizardFooter'

export default function StepTat({U, onSetTatFile, onBack, onNext}) {
  const toast = useToast()
  const [tat, setTat] = useState(() => buildSeededTat(U.code).tat)
  const [base] = useState(() => buildSeededTat(U.code).base)

  const setCell = (zoneKey, field, value) =>
    setTat((prev) => ({...prev, [zoneKey]: {...prev[zoneKey], [field]: value}}))

  const changedFlags = (z) => {
    const v = tat[z.key]
    const b = base[z.key]
    return {min: String(v.min) !== String(b.min), max: String(v.max) !== String(b.max)}
  }

  /* stats across the 5 zones */
  let setCnt = 0
  let dev = 0
  let err = 0
  let chg = 0
  TAT_ZONES.forEach((z) => {
    const v = tat[z.key]
    if (v.min !== '' && v.max !== '') {
      setCnt++
      if (parseFloat(v.min) > parseFloat(v.max)) err++
      else {
        const sc = stdCell(U.code, z.key)
        if (parseFloat(v.min) > +sc.min || parseFloat(v.max) > +sc.max) dev++
      }
    }
    const f = changedFlags(z)
    if (f.min || f.max) chg++
  })
  const tot = TAT_ZONES.length

  const deltaItems = TAT_ZONES.filter((z) => {
    const f = changedFlags(z)
    return f.min || f.max
  }).map((z) => {
    const v = tat[z.key]
    const b = base[z.key]
    return (
      <div className='delta-row' key={z.key}>
        <span className='dz'>{z.name}</span>
        <span className='old cut'>{b ? b.min + '–' + b.max + 'd' : '—'}</span>
        <span className='arr'><ArrowForwardIcon style={{width: 13, height: 13}} /></span>
        <span className='nw'>{v.min}–{v.max}d</span>
      </div>
    )
  })

  const validate = () => {
    if (err) {
      toast('Min cannot exceed Max — fix the highlighted zone card' + (err > 1 ? 's' : '') + '.', 'error')
      return false
    }
    if (setCnt < tot) {
      toast('Enter Min and Max TAT for every zone.', 'error')
      return false
    }
    const frac = TAT_ZONES.filter((z) => {
      const v = tat[z.key]
      return (v.min !== '' && +v.min % 1 !== 0) || (v.max !== '' && +v.max % 1 !== 0)
    }).length
    if (frac) {
      toast('TAT values must be whole days — fix the fractional value' + (frac > 1 ? 's' : '') + '.', 'error')
      return false
    }
    const csv = ['zone,Min Tat days,Max Tat Days']
      .concat(TAT_ZONES.map((z) => [z.n, tat[z.key].min, tat[z.key].max].join(',')))
      .join('\n')
    onSetTatFile(new File([csv], (U.code || 'tat') + '-tat.csv', {type: 'text/csv'}))
    return true
  }

  const renderZoneCard = (z, di) => {
    const v = tat[z.key]
    const sd = stdCell(U.code, z.key)
    const devMin = v.min !== '' && parseFloat(v.min) > +sd.min
    const devMax = v.max !== '' && parseFloat(v.max) > +sd.max
    const cellErr = v.min !== '' && v.max !== '' && parseFloat(v.min) > parseFloat(v.max)
    const ch = changedFlags(z)
    const bb = tatBand(v)
    const hot = devMin || devMax || cellErr
    return (
      <div className={'tatcard' + (cellErr ? ' err' : '')} style={{animationDelay: di * 0.05 + 's'}}>
        <div className='tat-eyebrow' title={z.desc}>
          <span className='zn'>ZONE {z.n}</span>
          <span className='zname'>{z.name}</span>
        </div>
        <div className='tat-row'>
          <label>Min</label>
          <input
            type='number'
            min='1'
            step='1'
            className={'input tati' + (ch.min ? ' changed' : '') + (devMin ? ' dev' : '')}
            value={v.min}
            placeholder='d'
            onChange={(ev) => setCell(z.key, 'min', ev.target.value)}
          />
        </div>
        <div className='tat-row'>
          <label>Max</label>
          <input
            type='number'
            min='1'
            step='1'
            className={'input tati' + (ch.max ? ' changed' : '') + (devMax ? ' dev' : '')}
            value={v.max}
            placeholder='d'
            onChange={(ev) => setCell(z.key, 'max', ev.target.value)}
          />
        </div>
        <div className='tat-scale'>
          <span className={'band' + (hot ? ' hot' : '')} style={{left: bb.l + '%', width: bb.w + '%'}} />
        </div>
        <div className='tat-std'>Std:&nbsp;{sd.min}–{sd.max}d</div>
      </div>
    )
  }

  return (
    <>
      <div className='card card-pad' style={{marginBottom: 16}}>
        <div
          className='row between center'
          style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4}}
        >
          <h3 style={{margin: 0}}>Revised Courier TAT</h3>
          <span className='row center gap8' style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
            {err ? <span className='badge b-error'>{err} invalid range{err > 1 ? 's' : ''}</span> : null}
            {chg ? <span className='badge b-info'>{chg} changed</span> : null}
            {dev ? <span className='badge b-pending'>{dev} slower than std</span> : null}
            {setCnt === tot && !err ? (
              <span className='badge b-active'><CheckIcon style={{width: 12, height: 12}} /> All zones set</span>
            ) : (
              <span className='badge b-info'>{setCnt} / {tot} zones</span>
            )}
          </span>
        </div>
        <p className='sub' style={{margin: '0 0 16px'}}>
          The grid is prefilled with the standard TAT band — edit only what changes. Edited cells turn
          amber; values slower than the standard range are flagged red but allowed.
        </p>

        <DeltaPanel title='Preview Δ — changed TAT zones' count={deltaItems.length} emptyMsg='No TAT changes yet — edit a zone below.'>
          {deltaItems}
        </DeltaPanel>

        <div className='tatsec'>
          <div className='row center gap8' style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 10px'}}>
            <span
              className='mono'
              style={{fontSize: 11, background: 'var(--row-alt)', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 8px'}}
            >
              {U.code}
            </span>
            {U.display && U.display !== U.code ? (
              <span className='muted' style={{fontSize: 12}}>{U.display}</span>
            ) : null}
          </div>
          <div className='tatgrid'>
            {TAT_ZONES.map((z, zi) => (
              <React.Fragment key={z.key}>{renderZoneCard(z, zi)}</React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <WizardFooter
        showBack
        last={false}
        onBack={onBack}
        onNext={() => {
          if (validate()) onNext()
        }}
      />
    </>
  )
}

StepTat.propTypes = {
  U: PropTypes.object,
  onSetTatFile: PropTypes.func,
  onBack: PropTypes.func,
  onNext: PropTypes.func,
}
